import { useState, useEffect, useCallback } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Loader2, Search, X, Check, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

/**
 * Admin-only dialog to manage cross-platform play links between MDB games.
 * Bidirectional — saving links here auto-syncs both directions on the backend.
 */
export default function CrossPlayLinksDialog({ open, onOpenChange, masterGameId, onSaved }) {
  const [game, setGame] = useState(null);
  const [linked, setLinked] = useState([]); // [{id, name, platforms, systems, boxArtUrl}]
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load current links when dialog opens
  useEffect(() => {
    if (!open || !masterGameId) return;
    setLoading(true);
    axios.get(`${API}/multiplayer/admin/games/${masterGameId}/cross-play-links`)
      .then(r => {
        setGame(r.data.game);
        setLinked(r.data.linked || []);
      })
      .catch(err => toast.error(err.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [open, masterGameId]);

  // Debounced search
  const runSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/multiplayer/admin/search-games`, {
        params: { q, exclude_id: masterGameId, limit: 20 },
      });
      // Filter out games already in `linked`
      const linkedIds = new Set(linked.map(l => l.id));
      setSearchResults((r.data || []).filter(g => !linkedIds.has(g.id)));
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [masterGameId, linked]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(searchQ), 250);
    return () => clearTimeout(t);
  }, [searchQ, runSearch]);

  const addLink = (g) => {
    setLinked(prev => [...prev, g]);
    setSearchResults(prev => prev.filter(x => x.id !== g.id));
  };

  const removeLink = (id) => {
    setLinked(prev => prev.filter(g => g.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/multiplayer/admin/games/${masterGameId}/cross-play-links`, {
        linked_ids: linked.map(g => g.id),
      });
      toast.success("Cross-play links saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const GameRow = ({ g, action, actionLabel }) => (
    <div key={g.id} className="flex items-center gap-2 p-2 rounded bg-white/[0.03] border border-white/5">
      {g.boxArtUrl && <img src={g.boxArtUrl} alt="" className="w-8 h-10 object-cover rounded shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-white text-sm truncate">{g.name}</div>
        <div className="text-white/40 text-xs truncate">
          {(g.systems || g.platforms || []).slice(0, 4).join(" · ") || "—"}
        </div>
      </div>
      {action}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-violet-400" />
            Cross-Play Links{game ? <span className="text-white/40 font-normal text-base">— {game.name}</span> : null}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
        ) : (
          <div className="space-y-4">
            <p className="text-white/40 text-xs">Link this MDB record to other records representing the same game on different platforms. Billboards and player lists merge across linked games. Changes apply bidirectionally to every game in the group.</p>

            {/* Currently linked */}
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2 font-medium">Currently linked ({linked.length})</p>
              {linked.length === 0 ? (
                <p className="text-white/30 text-xs italic">No linked records yet. Search below to add some.</p>
              ) : (
                <div className="space-y-1.5">
                  {linked.map(g => (
                    <GameRow
                      key={g.id}
                      g={g}
                      action={
                        <Button
                          data-testid={`cpl-remove-${g.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLink(g.id)}
                          className="text-white/40 hover:text-red-400 h-7 px-2"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div>
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2 font-medium">Add a link</p>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  data-testid="cpl-search"
                  placeholder="Search MDB by name..."
                  value={searchQ}
                  onChange={e => setSearchQ(e.target.value)}
                  className="pl-8 bg-white/5 border-white/10 text-white text-sm"
                />
                {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 animate-spin" />}
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-1.5 mt-2 max-h-72 overflow-y-auto">
                  {searchResults.map(g => (
                    <GameRow
                      key={g.id}
                      g={g}
                      action={
                        <Button
                          data-testid={`cpl-add-${g.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => addLink(g)}
                          className="text-emerald-400 hover:text-emerald-300 h-7 px-2"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                      }
                    />
                  ))}
                </div>
              )}
              {searchQ.trim() && !searching && searchResults.length === 0 && (
                <p className="text-white/30 text-xs italic mt-2">No more games match that search.</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/60 hover:text-white">Cancel</Button>
              <Button
                data-testid="cpl-save"
                onClick={handleSave}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save Links
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
